import queryString from "query-string";
import {
	API_SERVICE,
	DICTIONARIES_IN_API,
	GAME_TYPES,
	PAGES,
	UNSCRAMBLER_MAX_LENGTH,
	UNSCRAMBLER_MAX_WILDCARDS,
} from "@/common/consts";
import {
	trackEvent,
	replaceAccentChars,
	isEmpty,
	alphabetizeTiles,
	sanitizeTiles,
} from "@/common/functions";
import { getBucketId } from "@/common/feature-toggling";
import wordfinder from "@/common/wordfinder";

function serializeWordData(arr, statePropertyName) {
	return arr.reduce((acc, item) => {
		acc[`${item.word_length}${statePropertyName}`] = item;
		return acc;
	}, {});
}

export default {
	/** Commit a toggleBurger mutation */
	toggleBurger: function(context) {
		context.commit("toggleBurger");
	},
	/** Commit a closeBurger mutation */
	closeBurger: function(context) {
		context.commit("closeBurger");
	},
	/**
	 * Call Unscrambler service with user's query and commit response to
	 * loadWords mutations. This is done via executeQuery actions every time we
	 * navigate to an Unscrambler-like results page in router's beforeEach 3.1
	 * step.
	 */
	fetchWords: function(context, params = {}) {
		const containsEndpoint = "contains";
		const anagramsEndpoint = "unscrambler";
		params.limit = params.limit || "0";
		params.dictionary_opt = "YDR"; // Report whether words exist in YDR
		params.order_by = "score";

		let options = context.getters.getAdvancedSearchOptions;
		Object.assign(params, options);

		const gameType = params.gameType;
		const anagram = params.anagram;
		const isBonus = params.bonus === true;
		params.bonus = `${isBonus}`;
		delete params.gameType;
		delete params.anagram;

		context.commit("startLoading");

		//Verify if request comes from exact-match query, if so use correct method to process.
		let apiMethod = context.getters.getExact
			? containsEndpoint
			: anagramsEndpoint;
		if (apiMethod === containsEndpoint) {
			params.dictionary = DICTIONARIES_IN_API.US;
			delete params.bonus;
		}

		return wordfinder
			.request({
				url: `/v1/${apiMethod}?${queryString.stringify(params)}`,
				method: "get",
			})
			.then(
				(response) => {
					// If it's scrabble results page append up to 2 wildcards when no results were found and retry.
					if (
						gameType === GAME_TYPES.SCRABBLE &&
						!params.tiles.match(new RegExp("__", "g")) &&
						!response.data.data._items.length
					) {
						// Egde case: If it's a two-letter combination and one of the letters is K, append 2 wildcards instead of 1.
						let isSpecialCase =
							!!params.tiles.match(/^[a-z]k$/i) ||
							!!params.tiles.match(/^k[a-z]$/i);

						context.commit("updateSecondQueryFlag", true);
						context.dispatch("fetchWords", {
							tiles: isSpecialCase
								? `__${params.tiles}`
								: `_${params.tiles}`,
							anagram,
							gameType,
							bonus: isBonus,
							dictionary: params.dictionary,
						});
					} else {
						context.commit("loadWords", {
							words: response.data.data._items,
							resultsCount: response.data.data._meta.total,
							gameType,
							bonus: isBonus,
						});
						context.commit("stopLoading");
					}
				},
				(error) => {
					context.commit("errorFlag");
					context.commit("stopLoading");
				}
			);
	},
	/** Prepare and execute a user query, basically deciding if tiles need to
	 * be alphabetized as an Anagram (almost always) and dispatch fetch
	 */
	executeQuery: function(context, options) {
		let tiles;
		const anagram = options.anagram;
		if (anagram) {
			tiles = context.getters.getAnagram;
		} else {
			tiles = context.getters.getTiles;
		}

		// Reset the secondQuery flag, since anything initiated by this action can be safely assumed to be a primary query.
		context.commit("updateSecondQueryFlag", false);
		context.dispatch("fetchWords", {
			tiles,
			anagram,
			gameType: options.gameType,
			dictionary: options.dictionary,
			bonus: options.bonus,
		});
	},
	/** Track a Google Event. */
	trackEvent: function(context, payload) {
		const {
			ga,
			eventCategory,
			eventAction,
			eventLabel,
			eventValue,
			eventID,
		} = payload;
		const eventData = {
			eventCategory,
			eventAction,
			eventLabel,
			eventValue,
			eventID,
		};

		const expChannel = context.getters.getExperimentChannel;
		const expDate = context.getters.getExperimentDate;

		if (expChannel && expDate) {
			eventData.dimension2 = `v_${expChannel}`;
		}

		const bucketId = getBucketId();
		if (bucketId) {
			eventData.metric1 = bucketId;
		}

		const toggles = context.state?.features?.toggles || [];
		if (toggles) {
			const dimensionStr = `${
				bucketId ? `${bucketId}:` : ""
			}${toggles.join(",")}`;
			eventData.dimension4 = dimensionStr;
		}

		// we need eventID only for debugging and search for specific GA events
		delete eventData.eventID;
		trackEvent(ga, eventData);
	},
	removeSavedWord: function(context, payload) {
		return new Promise((resolve) => {
			let removeWordIndex = context.state.savedWords.findIndex(
				(savedWord) => savedWord.word === payload
			);
			context.commit("removeSavedWord", removeWordIndex);
			resolve();
		});
	},
	getWords: function(context, params) {
		if (params.tiles && params.tiles.length > UNSCRAMBLER_MAX_LENGTH) {
			return;
		}

		// If this is an additional query, no need to go into loading mode.
		if (params.offset === 0) {
			context.commit("startLoading");
		}

		const isWordList = params.gameType === GAME_TYPES.WORDSLISTS;
		let endPoint = isWordList ? "wordlist" : "unscrambler";

		if (params.tiles) {
			params.tiles = sanitizeTiles(
				params.tiles,
				context.state.navigation.current,
				false,
				true
			);

			params.tiles = alphabetizeTiles(
				params.tiles,
				context.state.navigation.current
			);
		}

		if (isWordList) {
			params.has_definition = "check";
		}

		if (!isWordList) {
			params.dictionary_opt = "DEF";
		}

		const gameType = params.gameType;
		const statePropertyName = params.statePropertyName || "words";
		delete params.gameType;
		delete params.statePropertyName;

		let isBonus;

		params.dictionary = context.getters.getCurrentDictionary.value;

		if (gameType === GAME_TYPES.UNSCRAMBLE) {
			isBonus = params.bonus === true;
			params.dictionary_opt = "YDR"; // Report whether words exist in YDR
			params.bonus = "true";
			let options = context.getters.getAdvancedSearchOptions;
			Object.assign(params, options);

			if (
				(context.state.navigation.current ===
					PAGES.UNSCRAMBLER_RESULTS ||
					context.state.navigation.current ===
						PAGES.SCRABBLE_DICT_RESULTS) &&
				context.getters.countWildcards === 0 &&
				params.dictionary !== DICTIONARIES_IN_API.ALL
			) {
				params.check_exact_match = true;
				params.exclude_original = true;
				params.original_tiles = context.state.query.tiles;
			}
		}

		params.tiles = isEmpty(context.state.query.prefixedTiles)
			? params.tiles
			: context.state.query.prefixedTiles;

		return wordfinder
			.get(`v1/${endPoint}`, { params })
			.then((response) => {
				const { data } = response.data;

				/**
				 * groupedBy and key are two variables that
				 * inform what kind of API query this is.
				 *
				 * If groupedBy is not null, this is a primary
				 * query for a grouped result (i.e. results are
				 * grouped by word length)
				 *
				 * If groupedBy is null and key is null, this is
				 * either a primary or secondary query for an
				 * ungrouped result (i.e. total words)
				 *
				 * If groupedBy is null and key is not null, this
				 * is either a primary or secondary query (usually
				 * secondary) for a subgroup of a grouped result
				 * (e.g. word length = 9)
				 *
				 * For both the last 2 scenarios, the meta offset
				 * value will tell you whether this is a primary or
				 * secondary query. An offset value of 0 means
				 * primary.
				 */
				const isGrouped =
					!!data._meta.group_by || !!data._groups || null;
				let keyName = params.word_length
					? `${data._meta.word_length}${statePropertyName}`
					: data._meta.word_length;
				const key =
					context.state.query.sortResults === "totalpoints"
						? null
						: keyName;

				const payload = {
					...params,
					key,
					gameType,
					data: isGrouped
						? serializeWordData(data._groups, statePropertyName)
						: data,
					isGrouped,
					statePropertyName,
				};

				if (gameType === GAME_TYPES.UNSCRAMBLE) {
					payload.bonus = isBonus;
				}

				context.commit("setWords", payload);
				if (data._augment && data._augment._meta.total) {
					payload.data = isGrouped
						? serializeWordData(
								data._augment._groups,
								"augmentedResults"
						  )
						: data._augment;
					payload.statePropertyName = "augmentedResults";
					context.commit("setWords", payload);
					context.commit(
						"setAugmentedResultsTiles",
						data._augment._meta.augment_tiles
					);
				}
				if (statePropertyName === "words") {
					context.commit(
						"updateResultsExactMatch",
						data._exact_match
					);
					if (params.offset === 0) {
						context.commit("updateResultsCount", data._meta.total);
					}
				}
			})
			.catch(() => context.commit("errorFlag"))
			.finally(() => {
				context.commit("stopLoading");
			});
	},
	resetWords: function(context) {
		context.commit("setWords", null);
	},
};
